import { type ApolloError, useApolloClient, useMutation } from '@apollo/client';
import { useCallback } from 'react';
import {
    CHANGE_COMPONENT_CREATE_REQUEST_STATUS,
    CREATE_COMPONENT_CREATE_REQUEST,
    UPDATE_COMPONENT_CREATE_REQUEST,
} from '@api/mutations/components';
import { GET_COMPONENTS_CREATE_REQUESTS } from '@api/queries/components';
import { type NewComponentRequestFormValue } from '@components/NewComponentCreateRequestTrigger/components/NewComponentsRequestDialog/components/NewComponentRequestForm';
import { mapFormValueToRequestInput } from '@components/NewComponentCreateRequestTrigger/components/NewComponentsRequestDialog/utils/mapFormValueToRequestInput';
import { useToast } from '@contexts/GlobalUiContext';
import {
    ComponentCreateRequestStatusCode,
    type CreateComponentRequestViewFragment,
    type CreateComponentCreateRequestRawInput,
    type ReviewComponentCreateRequestRawInput,
} from '@gql/graphql';
import { type BaseMutation, type BaseMutations } from '@models/BaseMutations';
import { type UpdateComponentRequestStatusForm } from '@pages/notification-components/components/RequestsCard/RequestsTable/components/ActionDialog';
import { setCachedField } from '@utils/apollo-cache-utils';
import { CREATE_COMPONENT_REQUEST_VIEW_FRAGMENT } from '@api/fragments/comments';

type PartialForm = Partial<NewComponentRequestFormValue>;

type Create = BaseMutation<PartialForm>;
type Update = BaseMutation<PartialForm>;

export type NewComponentRequestMutations = BaseMutations<Create, Update> & {
    updateStatus: BaseMutation<Partial<UpdateComponentRequestStatusForm>>;
};

export function useNewComponentRequestActions(): NewComponentRequestMutations {
    const client = useApolloClient();
    const { pushSuccessToast, pushErrorToast } = useToast();

    const refreshRequests = useCallback((): void => {
        void client.refetchQueries({
            include: [GET_COMPONENTS_CREATE_REQUESTS],
        });
    }, [client]);

    const handleError = useCallback(
        (error: ApolloError): void => {
            const message = error.message ?? 'Unknown error';
            pushErrorToast({
                detail: `Error occurred on component create request: ${message}`,
            });
        },
        [pushErrorToast],
    );

    const updateReviewedRequestInCache = useCallback(
        (
            requestId: string,
            nextStatusCode: ComponentCreateRequestStatusCode,
            nextStatusId: string,
            comment: string,
            nextSourceId?: string,
            nextComponentId?: string,
        ): void => {
            const entity = {
                id: requestId,
                __typename: 'ComponentCreateRequestView',
            } as const;
            const cachedRequest =
                client.readFragment<CreateComponentRequestViewFragment>({
                    id: client.cache.identify(entity),
                    fragment: CREATE_COMPONENT_REQUEST_VIEW_FRAGMENT,
                    fragmentName: 'CreateComponentRequestViewFragment',
                });

            if (!cachedRequest) {
                return;
            }

            const nextFields = {
                statusID: nextStatusId,
                statusCode: nextStatusCode,
                sourceID: nextSourceId ?? cachedRequest.sourceID,
                componentID: nextComponentId ?? cachedRequest.componentID,
                comments: [
                    ...cachedRequest.comments,
                    {
                        __typename: 'ComponentCreateRequestViewComment',
                        comment,
                        userID: cachedRequest.userID,
                        createTime: new Date().toISOString(),
                    },
                ],
            } as const;

            Object.entries(nextFields).forEach(([field, value]) => {
                setCachedField(client.cache, entity, field, value);
            });
        },
        [client.cache],
    );

    const [create] = useMutation(CREATE_COMPONENT_CREATE_REQUEST, {
        onCompleted: () => {
            refreshRequests();
            pushSuccessToast({
                detail: 'Component request created successfully',
            });
        },
        onError: handleError,
    });

    const [update] = useMutation(UPDATE_COMPONENT_CREATE_REQUEST, {
        onCompleted: () => {
            refreshRequests();
            pushSuccessToast({
                detail: 'Component request update successfully',
            });
        },
        onError: handleError,
    });

    const [updateStatus] = useMutation(CHANGE_COMPONENT_CREATE_REQUEST_STATUS, {
        onCompleted: () => {
            refreshRequests();
            pushSuccessToast({
                detail: 'Component request status updated successfully',
            });
        },
        onError: handleError,
    });

    const createHandler = useCallback(
        async (
            formValue: Partial<NewComponentRequestFormValue>,
        ): Promise<void> => {
            await create({
                variables: {
                    input: mapFormValueToRequestInput(
                        formValue,
                    ) as CreateComponentCreateRequestRawInput,
                },
            });
        },
        [create],
    );

    const updateHandler = useCallback(
        async (
            formValue: Partial<NewComponentRequestFormValue>,
        ): Promise<void> => {
            const { id, ...rest } = formValue;
            if (!id) {
                throw Error('ID is required');
            }

            const mappedInput = mapFormValueToRequestInput(rest);
            await update({
                variables: {
                    id,
                    input: {
                        ...mappedInput,
                    },
                },
            });
        },
        [update],
    );

    const updateStatusHandler = useCallback(
        async (
            formValue: Partial<UpdateComponentRequestStatusForm>,
        ): Promise<void> => {
            const {
                componentCreateRequest,
                action,
                comment,
                source,
                componentID,
            } = formValue;
            if (!componentCreateRequest) {
                throw Error('Modified component create request not found');
            }

            if (!comment) {
                throw Error('Comment is required');
            }

            if (!action) {
                throw Error('Action is required');
            }

            const proposedStatusID =
                componentCreateRequest.statusTransitions.find(
                    (transition) => transition.code === action,
                )?.id;
            if (!proposedStatusID) {
                throw Error('Proposed status id not found');
            }

            const additionalInputs: Partial<ReviewComponentCreateRequestRawInput> =
                {};

            if (action === ComponentCreateRequestStatusCode.Replaced) {
                additionalInputs.componentID = componentID;
            }

            if (action === ComponentCreateRequestStatusCode.Forwarded) {
                additionalInputs.componentSourceID = source;
            }

            await updateStatus({
                variables: {
                    input: {
                        id: componentCreateRequest.id,
                        comment,
                        proposedStatusID,
                        ...additionalInputs,
                    },
                },
                update: () => {
                    updateReviewedRequestInCache(
                        componentCreateRequest.id,
                        action,
                        proposedStatusID,
                        comment,
                        source,
                        componentID,
                    );
                },
            });
        },
        [updateReviewedRequestInCache, updateStatus],
    );

    const deleteHandler = useCallback(() => {
        throw Error('Delete operation is not supported for component requests');
    }, []);

    return {
        create: createHandler,
        update: updateHandler,
        updateStatus: updateStatusHandler,
        delete: deleteHandler,
    };
}
